import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { HireRequest } from '../lib/types';
import { submitHire } from '../lib/api';

const initialForm: HireRequest = {
  contact_name: '',
  phone: '',
  email: '',
  service_address: '',
  city: '',
  zip: '',
  county: '',
  service_type: 'one-time-mow',
  frequency: 'one-time',
  lot_size: '',
  preferred_guardian: '',
  notes: '',
  how_heard: '',
  consent_contact: false,
  honeypot: '',
};

const inputClass =
  'w-full bg-transparent border-b-2 border-forest/20 focus:border-terracotta focus:outline-none py-3 text-forest placeholder-forest/40 transition-colors';
const labelClass = 'block text-xs tracking-[0.2em] uppercase text-forest/70 mb-2';

const SERVICE_TYPES: Array<[HireRequest['service_type'], string, string]> = [
  ['one-time-mow', 'One-time mow', 'Single visit'],
  ['weekly', 'Weekly maintenance', 'Mow, edge, blow weekly'],
  ['biweekly', 'Biweekly maintenance', 'Every two weeks'],
  ['monthly', 'Monthly maintenance', 'Once a month'],
  ['cleanup', 'Cleanup / overgrown rescue', 'Heavy first cut, debris'],
  ['hardscape', 'Hardscape / materials', 'Stone, gravel, sod install'],
  ['sod', 'Sod installation', 'New St. Augustine lawn'],
  ['other', 'Something else', 'Tell us what you need'],
];

export function HirePage() {
  const [form, setForm] = useState<HireRequest>(initialForm);
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitError, setSubmitError] = useState<string | null>(null);
  const navigate = useNavigate();

  function update<K extends keyof HireRequest>(field: K, val: HireRequest[K]) {
    setForm((prev) => ({ ...prev, [field]: val }));
    if (errors[field as string]) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next[field as string];
        return next;
      });
    }
  }

  function validate(): boolean {
    const e: Record<string, string> = {};
    if (!form.contact_name.trim()) e.contact_name = 'Required';
    if (!form.phone.trim()) e.phone = 'Required';
    else if (form.phone.replace(/\D/g, '').length < 10) e.phone = 'At least 10 digits';
    if (!form.email.trim()) e.email = 'Required';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = 'Invalid email';
    if (!form.service_address.trim()) e.service_address = 'Required';
    if (!form.city.trim()) e.city = 'Required';
    if (!form.zip.trim()) e.zip = 'Required';
    setErrors(e);
    return Object.keys(e).length === 0;
  }

  async function submit() {
    setSubmitError(null);
    if (!validate()) {
      setSubmitError('Please fill in the highlighted fields.');
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }
    if (!form.consent_contact) {
      setSubmitError('Please confirm we can contact you to schedule.');
      return;
    }
    setSubmitting(true);
    const result = await submitHire(form);
    setSubmitting(false);
    if (!result.ok) {
      setSubmitError(result.error || 'Something went wrong. Please try again.');
      return;
    }
    navigate('/thank-you/hire');
  }

  const errClass = (field: string) => (errors[field] ? 'border-terracotta' : '');

  return (
    <div className="max-w-6xl mx-auto px-6 lg:px-12 py-16">
      <div className="grid lg:grid-cols-12 gap-12">
        <aside className="lg:col-span-4">
          <div className="lg:sticky lg:top-28">
            <div className="text-xs tracking-[0.25em] uppercase text-terracotta mb-3">Support a Mowkin</div>
            <h1
              className="text-5xl text-forest mb-6 leading-tight font-display"
              style={{ fontVariationSettings: '"opsz" 144' }}
            >
              Hire a Mowkin. <em>Sustain a mow.</em>
            </h1>
            <p className="text-forest/75 leading-relaxed mb-8">
              Need lawn care? Hire one of our Mowkin providers. Every paid
              service request routes to our network first — keeping their
              schedules full so they can keep donating free mows to neighbors
              who need them.
            </p>

            <div className="bg-forest text-cream p-6">
              <div className="text-xs tracking-[0.2em] uppercase text-sand mb-3">Why this matters</div>
              <ul className="text-sm leading-relaxed text-cream/85 space-y-3">
                <li className="flex gap-3">
                  <span className="text-sand shrink-0">→</span>
                  <span>Your business funds the volunteer hours.</span>
                </li>
                <li className="flex gap-3">
                  <span className="text-sand shrink-0">→</span>
                  <span>You get a vetted, insured local pro.</span>
                </li>
                <li className="flex gap-3">
                  <span className="text-sand shrink-0">→</span>
                  <span>No fee, no markup — we just route the lead.</span>
                </li>
              </ul>
            </div>
          </div>
        </aside>

        <div className="lg:col-span-8 bg-cream border border-forest/10 p-8 lg:p-12 space-y-10">
          {submitError && (
            <div className="bg-terracotta/10 border-l-4 border-terracotta p-4 text-terracotta text-sm">
              {submitError}
            </div>
          )}

          <section>
            <h2 className="text-2xl text-forest mb-6 font-display">01 — What do you need?</h2>
            <div className="grid sm:grid-cols-2 gap-3">
              {SERVICE_TYPES.map(([val, title, desc]) => (
                <button
                  key={val}
                  type="button"
                  onClick={() => update('service_type', val)}
                  className={`p-4 border-2 text-left transition-colors ${
                    form.service_type === val
                      ? 'border-terracotta bg-terracotta/5'
                      : 'border-forest/15 hover:border-terracotta/50'
                  }`}
                >
                  <div className="text-forest font-medium">{title}</div>
                  <div className="text-forest/60 text-xs mt-1">{desc}</div>
                </button>
              ))}
            </div>
          </section>

          <section>
            <h2 className="text-2xl text-forest mb-6 font-display">02 — Your contact info</h2>
            <div className="grid sm:grid-cols-2 gap-6">
              <div className="sm:col-span-2">
                <label className={labelClass}>Full name *</label>
                <input
                  className={`${inputClass} ${errClass('contact_name')}`}
                  value={form.contact_name}
                  onChange={(e) => update('contact_name', e.target.value)}
                  placeholder="Your name"
                />
                {errors.contact_name && <div className="text-terracotta text-xs mt-1">{errors.contact_name}</div>}
              </div>
              <div>
                <label className={labelClass}>Phone *</label>
                <input
                  className={`${inputClass} ${errClass('phone')}`}
                  value={form.phone}
                  onChange={(e) => update('phone', e.target.value)}
                  placeholder="(361) 555-0123"
                  inputMode="tel"
                />
                {errors.phone && <div className="text-terracotta text-xs mt-1">{errors.phone}</div>}
              </div>
              <div>
                <label className={labelClass}>Email *</label>
                <input
                  className={`${inputClass} ${errClass('email')}`}
                  value={form.email}
                  onChange={(e) => update('email', e.target.value)}
                  placeholder="you@example.com"
                  inputMode="email"
                />
                {errors.email && <div className="text-terracotta text-xs mt-1">{errors.email}</div>}
              </div>
            </div>
          </section>

          <section>
            <h2 className="text-2xl text-forest mb-6 font-display">03 — Where's the property?</h2>
            <div className="grid sm:grid-cols-2 gap-6">
              <div className="sm:col-span-2">
                <label className={labelClass}>Street address *</label>
                <input
                  className={`${inputClass} ${errClass('service_address')}`}
                  value={form.service_address}
                  onChange={(e) => update('service_address', e.target.value)}
                  placeholder="1234 Ocean Dr"
                />
                {errors.service_address && (
                  <div className="text-terracotta text-xs mt-1">{errors.service_address}</div>
                )}
              </div>
              <div>
                <label className={labelClass}>City *</label>
                <input
                  className={`${inputClass} ${errClass('city')}`}
                  value={form.city}
                  onChange={(e) => update('city', e.target.value)}
                  placeholder="Corpus Christi"
                />
                {errors.city && <div className="text-terracotta text-xs mt-1">{errors.city}</div>}
              </div>
              <div>
                <label className={labelClass}>ZIP *</label>
                <input
                  className={`${inputClass} ${errClass('zip')}`}
                  value={form.zip}
                  onChange={(e) => update('zip', e.target.value)}
                  placeholder="78411"
                  inputMode="numeric"
                />
                {errors.zip && <div className="text-terracotta text-xs mt-1">{errors.zip}</div>}
              </div>
              <div className="sm:col-span-2">
                <label className={labelClass}>County</label>
                <select
                  className={inputClass}
                  value={form.county}
                  onChange={(e) => update('county', e.target.value)}
                >
                  <option value="">Select...</option>
                  <option value="nueces">Nueces</option>
                  <option value="san-patricio">San Patricio</option>
                  <option value="aransas">Aransas</option>
                  <option value="other">Other</option>
                </select>
              </div>
              <div className="sm:col-span-2">
                <label className={labelClass}>Approximate lot size</label>
                <select
                  className={inputClass}
                  value={form.lot_size}
                  onChange={(e) => update('lot_size', e.target.value)}
                >
                  <option value="">Select if known...</option>
                  <option value="under_5000">Under 5,000 sq ft (small)</option>
                  <option value="5000_10000">5,000 – 10,000 sq ft (typical city lot)</option>
                  <option value="10000_20000">10,000 – 20,000 sq ft (large)</option>
                  <option value="20000_plus">Over 20,000 sq ft (acreage)</option>
                  <option value="unknown">Not sure</option>
                </select>
              </div>
            </div>
          </section>

          <section>
            <h2 className="text-2xl text-forest mb-6 font-display">04 — Details (optional)</h2>
            <div className="space-y-6">
              <div>
                <label className={labelClass}>Preferred Mowkin provider</label>
                <input
                  className={inputClass}
                  value={form.preferred_guardian}
                  onChange={(e) => update('preferred_guardian', e.target.value)}
                  placeholder="Company name if you have one in mind, or leave blank for first available"
                />
              </div>
              <div>
                <label className={labelClass}>How did you hear about us?</label>
                <input
                  className={inputClass}
                  value={form.how_heard}
                  onChange={(e) => update('how_heard', e.target.value)}
                  placeholder="Bay Area Landscape, a neighbor, social media..."
                />
              </div>
              <div>
                <label className={labelClass}>Anything else we should know?</label>
                <textarea
                  className={inputClass + ' resize-none'}
                  rows={4}
                  value={form.notes}
                  onChange={(e) => update('notes', e.target.value)}
                  placeholder="Gate access, pets, sprinkler heads, timing constraints, what you'd like done..."
                />
              </div>
            </div>
          </section>

          <section>
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={form.consent_contact}
                onChange={(e) => update('consent_contact', e.target.checked)}
                className="mt-1 accent-terracotta"
              />
              <span className="text-sm text-forest">
                Yes, you can contact me by phone or email to schedule a quote.{' '}
                <span className="text-terracotta">*</span>
              </span>
            </label>
          </section>

          {/* Honeypot */}
          <input
            type="text"
            name="website"
            tabIndex={-1}
            autoComplete="off"
            value={form.honeypot || ''}
            onChange={(e) => update('honeypot', e.target.value)}
            style={{ position: 'absolute', left: '-9999px', width: 1, height: 1, opacity: 0 }}
            aria-hidden="true"
          />

          <div className="pt-8 border-t border-forest/10 flex justify-end">
            <button
              onClick={submit}
              disabled={submitting}
              className="bg-terracotta text-cream px-10 py-4 text-sm tracking-[0.1em] uppercase hover:bg-forest transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {submitting ? 'Sending…' : 'Send Request →'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
